import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { CheckCircle, Edit, Save, UserPlus, X, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./ui/pagination";
import { app } from "../firebase";
import { useToast } from "../hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";

const ManageUsers = ({ user = null }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isMasterAdmin, setIsMasterAdmin] = useState(
    user?.role === "MASTERADMIN"
  );
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editFormData, setEditFormData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  const studentsPerPage = 12;

  const db = getFirestore(app);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, "BDUsers");
        const snapshot = await getDocs(usersCollection);
        const fetchedUsers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const uniqueUsersMap = new Map();
        fetchedUsers.forEach((user) => {
          if (!uniqueUsersMap.has(user.emailId)) {
            uniqueUsersMap.set(user.emailId, user);
          }
        });
        const uniqueUsers = Array.from(uniqueUsersMap.values());
        setUsers(uniqueUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchRoles = async () => {
      try {
        const rolesCollection = collection(db, "roles");
        const snapshot = await getDocs(rolesCollection);
        const fetchedRoles = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const rolesArray = fetchedRoles
          .filter((role) => isMasterAdmin || role.role !== "MASTERADMIN")
          .map((role) => role.role)
          .sort();
        setRoles(rolesArray);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
    fetchUsers();
  }, [db, isMasterAdmin]);

  const filteredUsers = users.filter(
    (user) =>
      user?.fullName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      user?.emailId?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      user?.role?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredUsers.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  );

  const handleEditClick = (user) => {
    setEditFormData({ ...user });
    setEditDialogOpen(true);
  };

  const handleCancelEdit = () => {
    setEditFormData(null);
    setEditDialogOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!editFormData) return;
    try {
      await updateDoc(doc(db, "BDUsers", editFormData.id), editFormData);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === editFormData.id ? editFormData : user
        )
      );
      setEditFormData(null);
      setEditDialogOpen(false);
      toast({
        title: "User updated",
        description: "The user's access details have been successfully updated.",
      });
    } catch (error) {
      console.error("Error updating user:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating the user.",
        variant: "destructive",
      });
    }
  };

  const handleFormChange = (field, value) => {
    setEditFormData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className="container py-3 space-y-6 max-w-7xl">
      <div className="flex justify-between items-center py-4 px-2">
        <h1 className="text-2xl font-bold">Manage Access & Roles</h1>
        <Button
          className="bg-[#0071bd] hover:bg-[#005fa3] border-[#0071bd] text-white"
          onClick={() => navigate('/admin/create-user')}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          User
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>User Accounts</CardTitle>
          <CardDescription>
            Manage user accounts and their access roles
          </CardDescription>
          <div className="mt-4">
            <Input
              placeholder="Search by name, email, role, or UID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                {/* <TableHead>UID</TableHead> */}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentStudents.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user?.fullName}
                  </TableCell>
                  <TableCell>{user.emailId}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {user?.status === "active" ? (
                      <Badge variant="success">Active</Badge>
                    ) : (
                      <Badge variant="destructive">Inactive</Badge>
                    )}
                  </TableCell>
                  {/* <TableCell>
                    {user?.uid ? (
                      <CheckCircle size={20} className="text-green-500" />
                    ) : (
                      <XCircle size={20} className="text-gray-400" />
                    )}
                  </TableCell> */}
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditClick(user)}
                        className="border-[#0071bd] text-[#0071bd] hover:bg-[#e6f1fa]"
                      >
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        className={
                          user.status === "active"
                            ? "bg-[#0071bd] hover:bg-[#005fa3] border-[#0071bd] text-white"
                            : "bg-[#d32f2f] hover:bg-[#b71c1c] border-[#d32f2f] text-white"
                        }
                        onClick={() => {
                          const newStatus =
                            user.status === "active" ? "inactive" : "active";
                          updateDoc(doc(db, "BDUsers", user.id), {
                            status: newStatus,
                          });
                          setUsers((prevUsers) =>
                            prevUsers.map((u) =>
                              u.id === user.id ? { ...u, status: newStatus } : u
                            )
                          );
                          toast({
                            title: "Status updated",
                            description: `User status has been ${
                              newStatus === "active" ? "activated" : "deactivated"
                            }.`,
                          });
                        }}
                      >
                        {user.status === "active" ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredUsers.length > 0 && (
            <div className="py-4 flex items-center justify-between border-t px-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{indexOfFirstStudent + 1}</span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastStudent, filteredUsers.length)}
                </span>{" "}
                of <span className="font-medium">{filteredUsers.length}</span>{" "}
                users
              </div>

              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(currentPage - 1)}
                      className={
                        currentPage === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>

                  {/* Conditionally render first page link */}
                  {currentPage > 3 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => handlePageChange(1)}>
                        1
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {/* Conditionally render ellipsis */}
                  {currentPage > 4 && (
                    <PaginationItem>
                      <span className="px-1 py-1">...</span>
                    </PaginationItem>
                  )}

                  {/* Page links */}
                  {Array.from(
                    {
                      length: Math.min(
                        5,
                        totalPages - Math.max(0, currentPage - 3)
                      ),
                    },
                    (_, i) => {
                      const pageNum = Math.max(1, currentPage - 2) + i;
                      return (
                        <PaginationItem key={i}>
                          <PaginationLink
                            onClick={() => handlePageChange(pageNum)}
                            isActive={pageNum === currentPage}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    }
                  )}

                  {/* Conditionally render ellipsis at the end */}
                  {currentPage < totalPages - 3 && (
                    <PaginationItem>
                      <span className="px-1 py-1">...</span>
                    </PaginationItem>
                  )}

                  {/* Conditionally render last page link */}
                  {currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationLink onClick={() => handlePageChange(totalPages)}>
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(currentPage + 1)}
                      className={
                        currentPage === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white rounded-xl shadow-2xl border border-gray-200" style={{ background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user details and permissions
            </DialogDescription>
          </DialogHeader>

          {editFormData && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label
                  htmlFor="name"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Name
                </label>
                <Input
                  id="name"
                  value={editFormData.fullName}
                  onChange={(e) => handleFormChange("fullName", e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={editFormData.emailId}
                  onChange={(e) => handleFormChange("emailId", e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <label
                  htmlFor="role"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Role
                </label>
                <Select
                  value={editFormData.role}
                  onValueChange={(value) => handleFormChange("role", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="space-y-0.5">
                  <label
                    htmlFor="status"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Status
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Toggle to enable or disable user access
                  </p>
                </div>
                <Switch
                  id="status"
                  checked={editFormData.status === "active"}
                  onCheckedChange={(checked) =>
                    handleFormChange("status", checked ? "active" : "inactive")
                  }
                />
              </div>

              <div className="bg-gray-100 p-2 rounded-md mt-2 border text-gray-700">
                <p className="text-xs">
                  <span className="font-semibold">ID:</span> <span className="font-mono">{editFormData.id}</span>
                </p>
                <p className="text-xs mt-1">
                  <span className="font-semibold">Last login:</span> {editFormData.lastLogin
                    ? new Date(editFormData.lastLogin.seconds * 1000).toLocaleString()
                    : "Never"
                  }
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={handleCancelEdit} className="border-[#0071bd] text-[#0071bd] hover:bg-[#e6f1fa]">
              <X size={16} className="mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-[#0071bd] hover:bg-[#005fa3] border-[#0071bd] text-white">
              <Save size={16} className="mr-1" /> Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageUsers;